import React, { useCallback, useEffect, useRef, useState } from 'react';
import { __ } from '@wordpress/i18n';

// icons
import PlayerPlayIcon from '../../../../icons/PlayerPlayIcon';
import PlayerPauseIcon from '../../../../icons/PlayerPauseIcon';

const formatTime = (seconds) => {
    const total = Math.max(0, Math.round(seconds || 0));
    const mm = Math.floor(total / 60);
    const ss = total % 60;

    return `${mm}:${String(ss).padStart(2, '0')}`;
}

/**
 * Preview of the clip attached to the cue an activity refers to. Rendered only
 * when the cue reference resolves to a cue sheet item carrying audio, so `url`
 * is always there.
 */
const CuePreview = ({ cue }) => {
    const audioRef = useRef(null);
    const trackRef = useRef(null);

    const [duration, setDuration] = useState(cue?.duration || 0);
    const [position, setPosition] = useState(0);
    const [playing, setPlaying] = useState(false);

    useEffect(() => {
        const audio = audioRef.current;

        if (!audio) return undefined;

        const onTime = () => setPosition(audio.currentTime);
        const onLoaded = () => setDuration(audio.duration || cue?.duration || 0);
        const onEnded = () => setPlaying(false);

        audio.addEventListener('timeupdate', onTime);
        audio.addEventListener('loadedmetadata', onLoaded);
        audio.addEventListener('ended', onEnded);

        return () => {
            audio.removeEventListener('timeupdate', onTime);
            audio.removeEventListener('loadedmetadata', onLoaded);
            audio.removeEventListener('ended', onEnded);
        };
    }, [cue?.duration]);

    const togglePlay = useCallback((e) => {
        e.stopPropagation();

        const audio = audioRef.current;

        if (!audio) return;

        if (audio.paused) {
            audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
        } else {
            audio.pause();
            setPlaying(false);
        }
    }, []);

    const seek = useCallback((e) => {
        e.stopPropagation();

        const rect = trackRef.current?.getBoundingClientRect();
        const audio = audioRef.current;

        if (!rect || !rect.width || !audio || !duration) return;

        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));

        audio.currentTime = ratio * duration;
        setPosition(audio.currentTime);
    }, [duration]);

    const progress = duration ? Math.min(100, (position / duration) * 100) : 0;

    return (
        <div className="cuePreview">
            <audio ref={audioRef} src={cue.url} preload="metadata"/>
            <button
                type="button"
                className="cuePreview__play"
                onClick={togglePlay}
                title={playing
                    ? __('Metti in pausa', 'soundbetween')
                    : __('Riproduci l’ultima versione', 'soundbetween')}>
                {playing ? <PlayerPauseIcon/> : <PlayerPlayIcon/>}
            </button>
            <div
                ref={trackRef}
                className="cuePreview__track"
                role="slider"
                tabIndex={0}
                aria-label={__('Posizione di riproduzione', 'soundbetween')}
                aria-valuemin={0}
                aria-valuemax={Math.round(duration)}
                aria-valuenow={Math.round(position)}
                onClick={seek}>
                <span className="cuePreview__progress" style={{ width: `${progress}%` }}/>
            </div>
            <span className="cuePreview__time">
                {formatTime(position)} / {formatTime(duration)}
            </span>
        </div>
    )
}

export default CuePreview;
